import * as ReactDOM from 'react-dom';

/**
 * The HostedStackedElementContainer allows clients to instantiate stacked elements from contexts
 * outside of a React component, allowing that stacked element to outlive its parent.
 *
 * The container can be reused; once the element is removed, another element can be passed in to
 * render. However, each container can hold only one stacked element at a time.
 *
 * The element passed to the container must involve the StackingComponent, otherwise it will not be
 * visibly rendered in the document.
 *
 * A common use case is a dialog box triggered from a menu; the menu action instantiates a container
 * and uses it to invoke the dialog. The container continues to live in memory even after the menu
 * item closes, providing a way for the dialog to be closed:
 *
 *   const container = new HostedStackedElementContainer();
 *   const dialogProps = {
 *     onCloseRequest: () => container.remove()
 *   };
 *   const dialogElement = el(DialogWidget, dialogProps);
 *   container.render(dialogElement);
 *
 * This component is not necessary when the parent component will outlive the stacked component; in
 * that case, the parent can simply render the stacked component as one of its children and remove
 * it when it should no longer be displayed.
 */
export class HostedStackedElementContainer {

  private internalEncapsulatedIsShown: boolean = false;
  private anchor: Element | null;

  /**
   * Renders the given stacked element into the document, hosted by this container
   */
  public render: (stackedReactElement: JSX.Element) => void = (stackedReactElement) => {
    if (this.internalEncapsulatedIsShown) {
      throw new Error('Cannot render the element; it is already shown');
    }

    this.anchor = document.createElement('div');
    ReactDOM.render(stackedReactElement, this.anchor);
    this.internalEncapsulatedIsShown = true;
  }

  /**
   * Removes the hosted element from the document
   */
  public remove: () => void = () => {
    if (this.internalEncapsulatedIsShown) {
      ReactDOM.unmountComponentAtNode(this.anchor!);
      this.anchor = null;
    }
    this.internalEncapsulatedIsShown = false;
  }

  public isShown: () => boolean = () => {
    return this.internalEncapsulatedIsShown;
  }
}
